import { useState, useMemo, useRef, useEffect } from 'react';
import { Search, ChevronDown, Check, X, Hash } from 'lucide-react';

interface Channel {
  id: string;
  name: string;
}

interface SearchableChannelSelectProps {
  value: string | null;
  onChange: (value: string | null) => void;
  channels: Channel[];
  placeholder?: string;
  className?: string;
}

export default function SearchableChannelSelect({
  value,
  onChange,
  channels,
  placeholder = 'Search or select channel...',
  className = '',
}: SearchableChannelSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Find currently selected channel object
  const selectedChannel = useMemo(() => {
    return channels.find((c) => c.id === value) || null;
  }, [value, channels]);

  // Sync display search input with selection when dropdown is closed
  useEffect(() => {
    if (!isOpen) {
      setSearch(selectedChannel ? `#${selectedChannel.name}` : '');
      setActiveIndex(-1);
    } else {
      // Clear input when opened so user can search immediately
      setSearch('');
    }
  }, [isOpen, selectedChannel]);

  // Filter channels based on search query
  const filtered = useMemo(() => {
    const query = search.replace(/^#/, '').toLowerCase().trim();
    if (!query) return channels;
    return channels.filter((c) => c.name.toLowerCase().includes(query));
  }, [channels, search]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        setActiveIndex((prev) => (prev + 1) % filtered.length);
        e.preventDefault();
        break;
      case 'ArrowUp':
        setActiveIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
        e.preventDefault();
        break;
      case 'Enter':
        if (activeIndex >= 0 && activeIndex < filtered.length) {
          onChange(filtered[activeIndex].id);
          setIsOpen(false);
        } else if (filtered.length > 0) {
          onChange(filtered[0].id);
          setIsOpen(false);
        }
        e.preventDefault();
        break;
      case 'Escape':
        setIsOpen(false);
        e.preventDefault();
        break;
      case 'Tab':
        setIsOpen(false);
        break;
    }
  };

  return (
    <div ref={containerRef} className={`relative min-w-[200px] ${className}`}>
      {/* Input Field */}
      <div className="relative group">
        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 flex items-center gap-1.5 text-gray-500 pointer-events-none group-focus-within:text-primary transition-colors">
          <Hash className="w-4 h-4" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? search : selectedChannel ? `#${selectedChannel.name}` : ''}
          onChange={(e) => {
            setSearch(e.target.value);
            setActiveIndex(-1);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selectedChannel ? `#${selectedChannel.name}` : placeholder}
          className="w-full pl-9 pr-16 py-2.5 bg-dark-800 border border-white/[0.08] focus:border-primary/50 rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none transition-colors"
        />

        {/* Clear Button */}
        {value && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
              setSearch('');
              inputRef.current?.focus();
            }}
            className="absolute right-9 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-white transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}

        {/* Dropdown Chevron */}
        <button
          type="button"
          onClick={() => {
            setIsOpen(!isOpen);
            inputRef.current?.focus();
          }}
          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
        >
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180 text-primary' : ''}`} />
        </button>
      </div>

      {/* Suggestion Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-dark-800/95 backdrop-blur-md border border-white/[0.08] rounded-xl shadow-2xl overflow-hidden animate-fade-in max-h-60 flex flex-col">
          {/* Header/Search Indicator */}
          <div className="flex items-center gap-2 px-3.5 py-2 border-b border-white/[0.04] bg-white/[0.01]">
            <Search className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-[11px] text-gray-500 font-medium uppercase tracking-wider">
              {search ? 'Filtering channels' : 'Available channels'}
            </span>
          </div>

          {/* List */}
          <div className="overflow-y-auto custom-scrollbar flex-1 py-1 max-h-48">
            {filtered.length === 0 ? (
              <div className="px-4 py-3 text-center text-sm text-gray-500">
                No matching channels
              </div>
            ) : (
              filtered.map((ch, idx) => {
                const isSelected = ch.id === value;
                const isActive = idx === activeIndex;

                return (
                  <button
                    key={ch.id}
                    type="button"
                    onClick={() => {
                      onChange(ch.id);
                      setIsOpen(false);
                    }}
                    onMouseEnter={() => setActiveIndex(idx)}
                    className={`w-full flex items-center justify-between px-3.5 py-2 text-sm text-left transition-colors ${
                      isSelected
                        ? 'bg-primary/20 text-primary font-medium'
                        : isActive
                        ? 'bg-white/[0.03] text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <span className="flex items-center gap-1.5 truncate">
                      <Hash className={`w-3.5 h-3.5 shrink-0 ${isSelected ? 'text-primary' : 'text-gray-600'}`} />
                      <span className="truncate">{ch.name}</span>
                    </span>
                    {isSelected && <Check className="w-4 h-4 text-primary shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
